import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { ApplicationCode } from '@app-env/environment.model';
import {
  ClaimsResponse,
  ClientConfigResponse,
  ContractHistoryResponse,
  DealerInfoResponse,
  DecodeVinResponse,
  ManualResponse,
  PendingContractResponse,
  RecentQuotes,
  RemittedContractResponse,
  ReportsResponse,
  ResourcesLinkRequest,
  ResourcesLinkResponse,
  ResourcesMenuResponse,
} from '@app-model/api';
import { Cart, CartItem, CartTax, LoadQuote, SaveQuote, ValidatedCart } from '@app-model/cart';
import { LienholderResponse, SubmitCartResponse } from '@app-model/checkout';
import { Forms } from '@app-model/form';
import { GetLookerReportLinkRequest } from '@app-model/get-looker-report-request.model';
import { LoginRequest, Markup, OktaLoginRequest, UserResponse } from '@app-model/login';
import {
  AggregatorRequest,
  GetContractHistoryRequest,
  GetPendingContractRequest,
  GetProductsRequest,
  GetRemittedContractRequest,
  IDeleteUserRequest,
  IForgotPasswordRequest,
  IForgotUsernameRequest,
  IGeneratePdfRequest,
  IGetAssociateDealerRequest,
  IGetClaimsRequest,
  IGetDealerInfoRequest,
  IGetReportsRequest,
  IGetResourcesMenuRequest,
  IGetUsersRequest,
  IRequestAccountRequest,
  ISupportEmailRequest,
  IUndoDeclineRequest,
  IUpdatePasswordRequest,
  RemitContractsRequest,
} from '@app-model/request';
import { IForgotOktaPasswordRequest } from '@app-model/request/forgot-okta-password.request';
import {
  ForgotPasswordFlow,
  GetPostalCodeResponse,
  GetProductsResponse,
  GetUserResponse,
  IGetAssociateDealersResponse,
} from '@app-model/response';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from './config.service';
import { TranslationObject } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _http = inject(HttpClient);
  private _configService = inject(ConfigService);

  private _oktaHeaders = new HttpHeaders({
    'Cache-Control': 'no-cache',
    'Content-Type': 'application/json',
  });

  login(request: LoginRequest): Observable<UserResponse> {
    return this._http
      .post<UserResponse>(`${this._configService.apiUrl}/login`, request)
      .pipe(catchError(this.errorHandler));
  }

  getOktaUser(oktaLoginRequest: OktaLoginRequest): Observable<UserResponse> {
    return this._http.post<UserResponse>(`${this._configService.apiUrl}/okta/getOktaUser`, oktaLoginRequest);
  }

  forgotOktaUserName({
    emailAddress: email,
    programId,
    application,
    captcha,
    captchaVersion,
  }: IForgotUsernameRequest): Observable<void> {
    return this._http.post<void>(
      `${this._configService.apiUrl}/okta/forgotusername`,
      { email, programId, application, captcha, captchaVersion },
      { headers: this._oktaHeaders }
    );
  }

  forgotOktaPassword(request: IForgotOktaPasswordRequest): Observable<{ success: boolean; status: boolean }> {
    return this._http
      .post<{ success: boolean; status: boolean }>(`${this._configService.apiUrl}/okta/forgotpassword`, request)
      .pipe(catchError(this.errorHandler));
  }

  resetOktaPassword(username: string, password: string, stateToken: string): Observable<void> {
    return this._http.post<void>(
      `${this._configService.apiUrl}/okta/resetpassword`,
      { username, stateToken, password },
      { headers: this._oktaHeaders }
    );
  }

  forgotPasswordOktaFlow(identifier: string): Observable<ForgotPasswordFlow> {
    return this._http
      .post<ForgotPasswordFlow>(`${this._configService.apiUrl}/okta/forgotpassword`, { identifier })
      .pipe(catchError(this.errorHandler));
  }

  validatePasswordResetToken(token): Observable<{ stateToken: string; username: string }> {
    return this._http.get<{ stateToken: string; username: string }>(
      `${this._configService.apiUrl}/okta/resetpasswordvalid/${token}`
    );
  }

  ssoLogin(request: { application: string }): Observable<UserResponse> {
    return this._http
      .post<UserResponse>(`${this._configService.apiUrl}/ssologin`, request)
      .pipe(catchError(this.errorHandler));
  }

  getProducts(request: GetProductsRequest): Observable<GetProductsResponse> {
    return this._http
      .post<GetProductsResponse>(`${this._configService.apiUrl}/products`, request)
      .pipe(catchError(this.errorHandler));
  }

  getProductsByUrl(url: string, request: AggregatorRequest): Observable<GetProductsResponse> {
    return this._http
      .post<GetProductsResponse>(`${this._configService.apiUrl}/aggregator/rates`, request)
      .pipe(catchError(this.errorHandler));
  }

  validateCart(request: Cart): Observable<ValidatedCart> {
    return this._http
      .post<ValidatedCart>(`${this._configService.apiUrl}/cartvalidation`, request)
      .pipe(catchError(this.errorHandler));
  }

  submitCart(request: unknown): Observable<SubmitCartResponse> {
    return this._http
      .post<SubmitCartResponse>(`${this._configService.apiUrl}/savecontract`, request)
      .pipe(catchError(this.errorHandler));
  }

  generatePDF(request: IGeneratePdfRequest): Observable<Blob> {
    request.pdfFormat = 'BINARY';

    return this._http
      .post(`${this._configService.apiUrl}/generatePdf`, request, { responseType: 'blob' as 'json' })
      .pipe(map((res: BlobPart) => new Blob([res], { type: 'application/pdf' })));
  }

  getClientInfo(application: string): Observable<ClientConfigResponse> {
    return this._http
      .get<ClientConfigResponse>(`${this._configService.apiUrl}/getclientinfo`, { params: { application } })
      .pipe(catchError(this.errorHandler));
  }

  saveMarkup(markup: Markup, username: string): Observable<void> {
    return this._http
      .post<void>(`${this._configService.apiUrl}/userdefaultadd`, { username, markup })
      .pipe(catchError(this.errorHandler));
  }

  editContract(contract: unknown): Observable<{ status: boolean }> {
    return this._http
      .post<{ status: boolean }>(`${this._configService.apiUrl}/updatependingcontract`, contract)
      .pipe(catchError(this.errorHandler));
  }

  deleteUser(user: IDeleteUserRequest): Observable<void> {
    return this._http.post<void>(`${this._configService.apiUrl}/deleteuser`, user).pipe(catchError(this.errorHandler));
  }

  getRecentQuotes(dealer: string): Observable<RecentQuotes[]> {
    return this._http
      .post<RecentQuotes[]>(`${this._configService.apiUrl}/getrecentquote`, { dealer })
      .pipe(catchError(this.errorHandler));
  }

  getPostal(request: { postalcode: string }): Observable<GetPostalCodeResponse> {
    return this._http
      .post<GetPostalCodeResponse>(`${this._configService.apiUrl}/getcitystate`, request)
      .pipe(catchError(this.errorHandler));
  }

  getUsers(request: IGetUsersRequest): Observable<GetUserResponse> {
    return this._http
      .post<GetUserResponse>(`${this._configService.apiUrl}/getusers`, request)
      .pipe(catchError(this.errorHandler));
  }

  getReports(request: IGetReportsRequest): Observable<ReportsResponse[]> {
    return this._http
      .post<ReportsResponse[]>(`${this._configService.apiUrl}/getreports`, request)
      .pipe(catchError(this.errorHandler));
  }

  getResourceMenu(request: IGetResourcesMenuRequest): Observable<ResourcesMenuResponse[]> {
    return this._http
      .post<ResourcesMenuResponse[]>(`${this._configService.apiUrl}/getresourcemenu`, request)
      .pipe(catchError(this.errorHandler));
  }

  getResourceLink(request: ResourcesLinkRequest): Observable<ResourcesLinkResponse> {
    return this._http
      .post<ResourcesLinkResponse>(`${this._configService.apiUrl}/getresourcelink`, request)
      .pipe(catchError(this.errorHandler));
  }

  // History Page - getting contracts
  getContractHistory(request: GetContractHistoryRequest): Observable<ContractHistoryResponse> {
    return this._http
      .post<ContractHistoryResponse>(`${this._configService.apiUrl}/getcontracthistory`, request)
      .pipe(catchError(this.errorHandler));
  }

  generateResourcePDF(id: string): Observable<Blob> {
    return this._http
      .post<Blob>(`${this._configService.apiUrl}/getpdf`, { id }, { responseType: 'blob' as 'json' })
      .pipe(map((res: BlobPart) => new Blob([res], { type: 'application/pdf' })));
  }

  getPendingContracts(request: GetPendingContractRequest): Observable<PendingContractResponse> {
    return this._http
      .post<PendingContractResponse>(`${this._configService.apiUrl}/getpendingcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  getRemittedContracts(request: GetRemittedContractRequest): Observable<RemittedContractResponse> {
    return this._http
      .post<RemittedContractResponse>(`${this._configService.apiUrl}/getremittedcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  submitPendingContracts(request: RemitContractsRequest): Observable<PendingContractResponse> {
    return this._http
      .post<PendingContractResponse>(`${this._configService.apiUrl}/remitcontracts`, request)
      .pipe(catchError(this.errorHandler));
  }

  getLookerReportLink(request: GetLookerReportLinkRequest): Observable<{ embedUrl?: string }> {
    return this._http
      .post<{ embedUrl?: string }>(`${this._configService.apiUrl}/looker/embeded-link`, request)
      .pipe(catchError(this.errorHandler));
  }

  decodeVin(request: { vin: string; dealer: string }): Observable<DecodeVinResponse> {
    return this._http
      .get<DecodeVinResponse>(`${this._configService.apiUrl}/vindecode`, { params: request })
      .pipe(catchError(this.errorHandler));
  }

  undoDecline(request: IUndoDeclineRequest): Observable<PendingContractResponse> {
    return this._http
      .post<PendingContractResponse>(`${this._configService.apiUrl}/undodecline`, request)
      .pipe(catchError(this.errorHandler));
  }

  getClaims(request: IGetClaimsRequest): Observable<ClaimsResponse> {
    return this._http
      .post<ClaimsResponse>(`${this._configService.apiUrl}/getclaims`, request)
      .pipe(catchError(this.errorHandler));
  }

  manualDecode(year: string = null, make: string = null, model: string = null): Observable<ManualResponse> {
    return this._http
      .get<ManualResponse>(`${this._configService.apiUrl}/manualdecode`, { params: { year, make, model } })
      .pipe(catchError(this.errorHandler));
  }

  refreshToken(): Observable<{ token: string }> {
    return this._http
      .get<{ token: string }>(`${this._configService.apiUrl}/refreshtoken`)
      .pipe(catchError(this.errorHandler));
  }

  saveQuote(quote: SaveQuote): Observable<void> {
    return this._http.post<void>(`${this._configService.apiUrl}/saveQuote`, quote).pipe(catchError(this.errorHandler));
  }

  loadQuote(request: { dealer: string; vin: string }): Observable<LoadQuote> {
    return this._http
      .post<LoadQuote>(`${this._configService.apiUrl}/getQuote`, request)
      .pipe(catchError(this.errorHandler));
  }

  loadQuoteOnly(request: { dealer: string; vin: string }): Observable<LoadQuote> {
    return this._http
      .post<LoadQuote>(`${this._configService.apiUrl}/getQuoteOnly`, request)
      .pipe(catchError(this.errorHandler));
  }

  deleteQuote(request: { dealer: string; vin: string }): Observable<unknown> {
    return this._http
      .post<unknown>(`${this._configService.apiUrl}/cartdelete`, request)
      .pipe(catchError(this.errorHandler));
  }

  getProductForm(application: ApplicationCode): Observable<Forms> {
    return this._http
      .post<Forms>(`${this._configService.apiUrl}/getproductform`, { application })
      .pipe(catchError(this.errorHandler));
  }

  getLienholder(lienholder: string): Observable<LienholderResponse[]> {
    return this._http
      .post<LienholderResponse[]>(`${this._configService.apiUrl}/getlienholder`, { lienholder })
      .pipe(catchError(this.errorHandler));
  }

  addLienholder(request: unknown): Observable<{ newLienholder: LienholderResponse }> {
    return this._http
      .post<{ newLienholder: LienholderResponse }>(`${this._configService.apiUrl}/addlienholder`, request)
      .pipe(catchError(this.errorHandler));
  }

  forgotUsername(request: IForgotUsernameRequest): Observable<{ status: boolean }> {
    return this._http
      .post<{ status: boolean }>(`${this._configService.apiUrl}/forgotusername`, request)
      .pipe(catchError(this.errorHandler));
  }

  forgotPassword(request: IForgotPasswordRequest): Observable<{ status: boolean }> {
    return this._http
      .post<{ status: boolean }>(`${this._configService.apiUrl}/forgotpassword`, request)
      .pipe(catchError(this.errorHandler));
  }

  getDealerInfo(request: IGetDealerInfoRequest): Observable<DealerInfoResponse[]> {
    return this._http
      .post<DealerInfoResponse[]>(`${this._configService.apiUrl}/getdealerinfo`, request)
      .pipe(catchError(this.errorHandler));
  }

  getDealer(request: { application: string; cmsDealerNumber: string }): Observable<DealerInfoResponse> {
    return this._http
      .post<DealerInfoResponse>(`${this._configService.apiUrl}/getdealer`, request)
      .pipe(catchError(this.errorHandler));
  }

  getassociateDealers(request: IGetAssociateDealerRequest): Observable<IGetAssociateDealersResponse> {
    return this._http
      .post<IGetAssociateDealersResponse>(`${this._configService.apiUrl}/getalldealers`, request)
      .pipe(catchError(this.errorHandler));
  }

  getCheckoutForm(request: Cart): Observable<Forms> {
    return this._http
      .post<Forms>(`${this._configService.apiUrl}/getcheckoutform`, request)
      .pipe(catchError(this.errorHandler));
  }

  requestAccount(request: IRequestAccountRequest): Observable<{ status: boolean }> {
    return this._http
      .post<{
        status: boolean;
      }>(`${this._configService.apiUrl}/requestaccount`, request)
      .pipe(catchError(this.errorHandler));
  }

  supportEmail(request: ISupportEmailRequest): Observable<void> {
    return this._http
      .post<void>(`${this._configService.apiUrl}/supportemail`, request)
      .pipe(catchError(this.errorHandler));
  }

  updatePassword(request: IUpdatePasswordRequest): Observable<{ status: boolean }> {
    return this._http
      .post<{ status: boolean }>(`${this._configService.apiUrl}/updatepassword`, request)
      .pipe(catchError(this.errorHandler));
  }

  taxCalculation(request: CartTax): Observable<{ cart: CartItem[] }> {
    return this._http
      .post<{ cart: CartItem[] }>(`${this._configService.apiUrl}/taxcalculation`, request)
      .pipe(catchError(this.errorHandler));
  }

  getTranslation(request: { application: string; language: string }): Observable<TranslationObject> {
    return this._http.get<TranslationObject>(this._getLanguagePath(request)).pipe(
      catchError((err) => {
        console.error(err);

        return this._http.get<TranslationObject>('../../../assets/i18n/en-US.json');
      })
    );
  }

  errorHandler(error: HttpErrorResponse): Observable<never> {
    return observableThrowError(error);
  }

  //////////////////////////////////////////////////////////////////////////////////
  //  Language defaults ////////////////////////////////////////////////////////////
  private _getLanguagePath(request: { language: string }): string {
    const language = request.language;
    switch (language) {
      case 'en-US':
      default: {
        return '../../../assets/i18n/en-US.json';
      }
      case 'fr-CA': {
        return '../../../assets/i18n/fr-CA.json';
      }
    }
  }
}
